import { JJIT_SNAPSHOT, type Job as RawJob } from "@/data/jjit-snapshot";

export type EnrichedJob = RawJob & {
  slug: string;
  role: RoleKey;
  shortDescription: string;
  responsibilities: string[];
  requirements: string[];
  niceToHave: string[];
  benefits: string[];
};

export type Job = EnrichedJob;

const INTERNAL_SOURCE_URL =
  "https://justjoin.it/job-offers/all-locations?companies=Green+Hive+Technology";

const slugify = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

/* ------------------------------------------------------------------ */
/* Role-aware enrichment                                              */
/* ------------------------------------------------------------------ */

export type RoleKey =
  | "product-owner"
  | "product-designer"
  | "data-analyst"
  | "data-scientist"
  | "data-architect"
  | "ml-engineer"
  | "genai"
  | "senior-llm-python"
  | "senior-fullstack-supplychain"
  | "systems-analyst"
  | "solutions-architect"
  | "senior-qa-playwright"
  | "senior-java-fintech"
  | "qa"
  | "java"
  | "fallback";

const detectRole = (title: string): RoleKey => {
  const t = title.toLowerCase();
  if (t.includes("senior software engineer") && t.includes("full stack")) return "senior-fullstack-supplychain";
  if (t.includes("senior software engineer") && (t.includes("llm") || t.includes("python"))) return "senior-llm-python";
  if (t.includes("senior qa")) return "senior-qa-playwright";
  if (t.includes("senior java")) return "senior-java-fintech";
  if (t.includes("data architect")) return "data-architect";
  if (t.includes("genai") || t.includes("gen ai") || t.includes("llm")) return "genai";
  if (t.includes("machine learning") || t.includes("ml engineer")) return "ml-engineer";
  if (t.includes("data scientist")) return "data-scientist";
  if (t.includes("data analyst") || t.includes("data analytics")) return "data-analyst";
  if (t.includes("solutions architect")) return "solutions-architect";
  if (t.includes("analityk") || t.includes("systems analyst")) return "systems-analyst";
  if (t.includes("product owner")) return "product-owner";
  if (t.includes("product designer") || t.includes("ux") || t.includes("designer")) return "product-designer";
  if (t.includes("qa") || t.includes("quality") || t.includes("test")) return "qa";
  if (t.includes("java")) return "java";
  return "fallback";
};

export type RoleProfile = {
  shortDescription: string;
  responsibilities: string[];
  requirements: string[];
  niceToHave: string[];
  benefits?: string[];
};

const ROLE_PROFILES: Record<RoleKey, RoleProfile> = {
  "product-owner": {
    shortDescription:
      "Own a product area end-to-end - from discovery and roadmap to delivery - partnering with engineering, design and business stakeholders to ship measurable outcomes.",
    responsibilities: [
      "Shape the product vision and quarterly roadmap for your domain",
      "Translate business goals into a prioritized, well-groomed backlog",
      "Run discovery with users, analytics and stakeholder interviews",
      "Lead refinement, planning and review ceremonies with the squad",
      "Define success metrics and report on outcomes after each release",
    ],
    requirements: [
      "3+ years as Product Owner or Product Manager in digital products",
      "Proven track record delivering complex features in agile teams",
      "Strong stakeholder management and communication skills",
      "Hands-on with backlog tools (Jira, Confluence) and analytics",
      "English at B2+ for daily collaboration with international peers",
    ],
    niceToHave: [
      "Experience in retail, e-commerce or omnichannel platforms",
      "Working knowledge of SQL or BI tooling for self-serve analytics",
      "Background in service design or UX research",
    ],
  },
  "product-designer": {
    shortDescription:
      "Design end-to-end product experiences across web and mobile - from research and information architecture through to polished, ship-ready UI in a mature design system.",
    responsibilities: [
      "Lead discovery, ideation and validation for assigned product areas",
      "Create flows, wireframes, prototypes and high-fidelity UI",
      "Contribute to and evolve the design system and shared components",
      "Run usability tests and synthesize insights into design decisions",
      "Pair tightly with PMs and engineers from concept to release",
    ],
    requirements: [
      "Portfolio with shipped product work, ideally B2C or SaaS",
      "Strong command of Figma, prototyping and component thinking",
      "Solid foundations in interaction and visual design",
      "Comfort running user interviews and synthesizing findings",
      "English at B2+ for cross-functional collaboration",
    ],
    niceToHave: [
      "Experience with analytics tools such as Amplitude or Mixpanel",
      "Motion design or front-end literacy (HTML / CSS)",
      "Prior work on design systems at scale",
    ],
  },
  "data-analyst": {
    shortDescription:
      "Turn product and business data into clear, actionable insights - building dashboards, deep-dives and experiments that drive decisions across the squad.",
    responsibilities: [
      "Define KPIs and build self-serve dashboards for product squads",
      "Run ad-hoc analyses, A/B test reads and funnel deep-dives",
      "Partner with PMs to design experiments and interpret results",
      "Maintain data quality, definitions and documentation",
      "Present findings to business stakeholders in plain language",
    ],
    requirements: [
      "Strong SQL and confident work with large analytical datasets",
      "Experience with a BI tool (Looker, Tableau, Power BI or similar)",
      "Solid grasp of product analytics and experimentation",
      "Working Python or R for ad-hoc analyses",
      "English at B2+ for written reports and presentations",
    ],
    niceToHave: [
      "Exposure to dbt, Snowflake, BigQuery or similar warehouses",
      "Experience with Amplitude, Mixpanel or comparable tools",
      "Background in retail, fintech or marketplace analytics",
    ],
  },
  "data-scientist": {
    shortDescription:
      "Frame business problems as data science questions and ship models that move the needle - from EDA and prototyping to production deployment with the platform team.",
    responsibilities: [
      "Scope and prioritize ML opportunities with product and business",
      "Build, validate and ship predictive and prescriptive models",
      "Design experiments to measure model and feature impact",
      "Work with engineering on production deployment and monitoring",
      "Mentor junior analysts and raise the analytical bar",
    ],
    requirements: [
      "Strong Python (pandas, scikit-learn) and SQL fundamentals",
      "Hands-on experience taking models from notebook to production",
      "Solid statistics, experimentation and model evaluation skills",
      "Comfort with cloud data platforms (AWS, GCP or Azure)",
      "English at B2+ for technical and stakeholder communication",
    ],
    niceToHave: [
      "Experience with MLOps tooling (MLflow, Kubeflow, SageMaker)",
      "Background with deep learning frameworks (PyTorch, TensorFlow)",
      "Prior leadership of cross-functional data initiatives",
    ],
  },
  "data-architect": {
    shortDescription:
      "Design, evolve and govern a scalable cloud data platform - warehouses, data lakes, pipelines and governance - that powers analytics, ML and product decisions across the business.",
    responsibilities: [
      "Design, evolve and optimize data warehouse architecture for scale, performance and business fit",
      "Define, implement and enforce architectural standards and best practices across data, file storage and real-time data flows",
      "Develop consistent data structures and naming conventions that keep solutions readable and maintainable",
      "Build and configure data-architect tooling such as data catalogs, and oversee ETL/ELT implementation and evolution",
      "Ensure high quality, consistency and usability of data used for business analytics",
      "Maintain data architecture coherence and align solutions with data governance, security and quality principles",
    ],
    requirements: [
      "Minimum 3 years of experience in data architecture and cloud solutions, especially GCP or Azure",
      "Hands-on experience with cloud data warehouses such as BigQuery or Snowflake and strong SQL skills",
      "Best-practice knowledge of designing, documenting and deploying data processing pipelines, including versioning, CI/CD and data testing",
      "Solid understanding of data modeling, ETL/ELT processes and integration from various data sources",
      "Knowledge of data quality, information security and data governance principles",
    ],
    niceToHave: [
      "Experience in designing system architecture",
      "Knowledge of streaming technologies and real-time data processing architectures",
    ],
    benefits: [
      "B2B contract with a competitive rate",
      "Offices available in Poznań and Warszawa",
      "Preferential benefits including MultiSport, Luxmed private healthcare and Uniqa group insurance",
      "Discounts on Maczfit, Samsung stores and car purchase or lease options",
      "Real impact on technologies used by millions of people every day",
    ],
  },
  "ml-engineer": {
    shortDescription:
      "Productionize machine learning at scale - owning data pipelines, model serving and observability so data science work reaches users reliably and quickly.",
    responsibilities: [
      "Design and maintain feature pipelines and training workflows",
      "Deploy and monitor models in batch and real-time serving paths",
      "Build reusable ML platform components for the data team",
      "Optimize model performance, latency and infrastructure cost",
      "Collaborate with data scientists from prototype to release",
    ],
    requirements: [
      "Strong Python and software engineering best practices",
      "Hands-on with one cloud (AWS, GCP or Azure) and IaC tooling",
      "Experience with containers, CI/CD and orchestration",
      "Solid knowledge of ML frameworks (scikit-learn, PyTorch, TF)",
      "English at B2+ for daily team collaboration",
    ],
    niceToHave: [
      "Streaming and event-driven architectures (Kafka, Pub/Sub)",
      "Experience with feature stores and ML observability",
      "Exposure to GenAI / LLM serving stacks",
    ],
  },
  genai: {
    shortDescription:
      "Build production-grade GenAI experiences - from prompt design and RAG pipelines to evaluation and safety - embedded in real product workflows.",
    responsibilities: [
      "Design and ship LLM-powered features end-to-end",
      "Build retrieval, prompting and orchestration pipelines",
      "Establish evaluation harnesses, guardrails and observability",
      "Benchmark models and iterate on cost / quality trade-offs",
      "Coach product teams on responsible GenAI patterns",
    ],
    requirements: [
      "Strong Python and software engineering fundamentals",
      "Hands-on with LLM tooling (OpenAI, Azure OpenAI, LangChain or similar)",
      "Experience with vector stores and RAG architectures",
      "Solid grasp of prompt engineering and evaluation methods",
      "English at B2+ for technical collaboration",
    ],
    niceToHave: [
      "Background in ML / NLP beyond LLMs",
      "Experience with fine-tuning or model distillation",
      "Familiarity with privacy and compliance for AI in production",
    ],
  },
  "systems-analyst": {
    shortDescription:
      "Bridge business and engineering - turning vague requirements into clear specifications, process maps and acceptance criteria the squad can build against.",
    responsibilities: [
      "Gather and structure business requirements with stakeholders",
      "Model processes (BPMN), data and integrations (UML)",
      "Write user stories and acceptance criteria for engineering",
      "Support QA on test scenarios and UAT",
      "Maintain living documentation in Confluence and Jira",
    ],
    requirements: [
      "Proven experience as systems / business analyst in IT projects",
      "Confident with BPMN, UML and requirement engineering practices",
      "Working knowledge of SQL for data exploration",
      "Strong analytical thinking and clear written communication",
      "English at B2+ for cross-team collaboration",
    ],
    niceToHave: [
      "Experience with retail / POS / cashier ecosystems",
      "Familiarity with API design and integration patterns",
      "Exposure to product analytics tooling (Amplitude, Mixpanel)",
    ],
  },
  "solutions-architect": {
    shortDescription:
      "Own the technical blueprint of a product platform - making the high-leverage choices on architecture, integrations and non-functional requirements.",
    responsibilities: [
      "Design end-to-end architecture for new and existing modules",
      "Define integration contracts and data flows across services",
      "Set non-functional standards: performance, security, scalability",
      "Mentor engineers and review critical design decisions",
      "Partner with product on technical roadmap and trade-offs",
    ],
    requirements: [
      "5+ years engineering experience with at least 2 in architecture",
      "Strong with one major stack (.NET / C#, Java or similar)",
      "Hands-on with cloud architecture and CI/CD pipelines",
      "Solid relational database design (PostgreSQL or equivalent)",
      "English at B2+ for documentation and cross-team work",
    ],
    niceToHave: [
      "Experience with retail platforms or omnichannel commerce",
      "Event-driven and microservices architectures at scale",
      "Cloud certifications (AWS, Azure or GCP)",
    ],
  },
  "senior-java-fintech": {
    shortDescription:
      "Build the backend of an API-first fintech platform - virtual IBANs, card issuing, domestic and international payments, core banking and AML/KYC - for a VC-backed, already profitable product company.",
    responsibilities: [
      "Design and implement backend services (Java, Spring Boot, Kafka, PostgreSQL)",
      "Contribute to system architecture and technical decision-making",
      "Build scalable, reliable and maintainable distributed systems",
      "Work with cloud infrastructure (AWS, Docker, CI/CD, Terraform)",
      "Take ownership of features and systems end-to-end",
      "Collaborate directly with leadership and other engineers - no middle layers",
    ],
    requirements: [
      "Minimum 5 years of experience as a Java Software Engineer",
      "Proven ability to deliver complete backend solutions independently",
      "Strong knowledge of Java, Spring Boot and PostgreSQL",
      "Experience with Kafka or similar event streaming platforms",
      "Solid understanding of AWS and modern DevOps practices",
      "High engineering standards, strong ownership mindset and good self-organization",
      "English at B2+ for daily work with a UK-based team",
    ],
    niceToHave: [
      "Fintech or payments background (not required)",
      "Frontend contributions in React and TypeScript",
      "DevOps workflows and infrastructure automation",
    ],
    benefits: [
      "Product company with real clients, already profitable and VC-backed",
      "API-first platform: payments, core banking, card issuing, AML/KYC",
      "Direct contact with decision makers - no middle layers",
      "100% remote work from Poland",
      "Fit-to-Win Sprint process: short feedback loops and a clear decision",
    ],
  },
  "senior-qa-playwright": {
    shortDescription:
      "Own software quality end-to-end - design and execute test strategies, build regression and smoke suites with Playwright, and partner with engineering on defect analysis, CI/CD integration and release readiness.",
    responsibilities: [
      "Design, implement and maintain automated tests in Playwright",
      "Develop test strategies, plans and coverage for web, API and integrations",
      "Build and maintain regression and smoke test suites",
      "Verify REST APIs (functional, integration, non-functional)",
      "Record testing metrics and ensure traceability in Jira/Confluence",
      "Support CI/CD integration, test reliability and release readiness",
      "Collaborate with developers on defect analysis, root cause and testability",
      "Work with Product on requirements, acceptance criteria and user personas",
    ],
    requirements: [
      "5+ years in QA engineering",
      "Proven experience with UI test automation in Playwright",
      "Strong understanding of REST APIs, OpenAPI/Swagger and API testing",
      "Basic knowledge of web technologies (HTML/CSS/JS, browser dev tools)",
      "Familiarity with CI/CD, Docker and agile environments (Scrum/Kanban)",
      "TypeScript and Python for test automation and API examples",
      "Bachelor's or Master's degree in Computer Science or related field",
      "Excellent English (verbal and written)",
    ],
    niceToHave: [
      "Good understanding of GenAI technologies (LLMs, multimodal, diffusion)",
      "Experience with performance or non-functional API testing",
      "Process improvement and quality coaching background",
    ],
    benefits: [
      "B2B contract with compensation based on seniority",
      "Full benefits package (Luxmed private healthcare, Multisport and more)",
      "26 days of paid vacation per year",
      "14 days of paid sick leave",
      "100% remote work from Poland",
      "Modern tooling, learning budget and a senior, no-nonsense team",
    ],
  },
  "senior-fullstack-supplychain": {
    shortDescription:
      "Join a YC W25 AI-native supply chain startup (aerospace, electronics, industrial, defense) - build backend-leaning full-stack features in TypeScript, React/Svelte and PostgreSQL, connecting legacy ERPs and email-driven workflows. 20-person team across Warsaw and San Francisco, $10M+ raised, seven-figure ARR. Full-time, on-site in Warsaw.",
    responsibilities: [
      "Build and maintain full-stack features, from database schema to user interface",
      "Integrate legacy ERP systems with the workflows customers use every day",
      "Build backend services in TypeScript, integrated with a frontend framework and PostgreSQL",
      "Connect email-driven processes that handle real customer orders",
      "Manage several projects in parallel and take each through to delivery",
      "Work closely with the platform team and forward deployed engineers",
    ],
    requirements: [
      "Ideally 5+ years of full-stack experience",
      "Independent system design experience and strong TypeScript skills",
      "Proficiency in a frontend framework such as React, Svelte or Next.js",
      "Ability to write, optimize and debug complex SQL queries beyond ORM usage",
      "Daily use of AI coding tools and awareness of their latest capabilities",
      "Fluent English; comfortable working with founders in their early twenties",
      "Strong work ethic, adaptability and low ego; curious, honest and outcome-focused",
      "On-site work in Warsaw and international travel up to one week per quarter",
    ],
    niceToHave: [
      "Internationally deployed SaaS products",
      "Experience in Series A-C startups",
    ],
    benefits: [
      "B2B contract with a competitive rate",
      "Possibility to co-living with the team at Warsaw hackerhouse",
      "Young team and bosses in their early twenties",
      "Fit-to-Win Sprint process: short feedback loops and a clear decision",
    ],
  },
  "senior-llm-python": {
    shortDescription:
      "Design, build and maintain web-based applications in Python with a strong LLM and multi-agent angle - owning quality, documentation and mentoring across the engineering team.",
    responsibilities: [
      "Own design, architecture, development, testing and maintenance of web apps",
      "Partner with Product Design and Product Management on specifications",
      "Implement new features and maintain the existing codebase",
      "Follow coding standards and engineering best practices",
      "Produce quality, clean, tested code (unit, integration and other QA tests)",
      "Create, maintain and own code and application documentation",
      "Support engineering and support teams in debugging and troubleshooting",
      "Coach and mentor junior software engineers on the team",
    ],
    requirements: [
      "7+ years building commercial-grade web applications in Python",
      "Strong proficiency with data-driven web applications",
      "Practical use of SOLID and Domain-Driven Design principles",
      "Experience with LLMs and NLP technologies in multi-agent systems",
      "2+ years of competitive web development in Scrum/Kanban/Agile",
      "Basic knowledge of machine learning methods and algorithms",
      "SQL database design and support (MySQL, MariaDB, Postgres)",
      "Very good English (verbal and written)",
    ],
    niceToHave: [
      "Experience leading or mentoring cross-functional engineering teams",
      "Background in retrieval, agentic orchestration or evaluation frameworks",
      "Exposure to MLOps tooling and production model serving",
    ],
    benefits: [
      "B2B contract with compensation based on seniority",
      "Full benefits package (Luxmed private healthcare, Multisport and more)",
      "26 days of paid vacation per year",
      "14 days of paid sick leave",
      "100% remote work from Poland",
      "Modern tooling, learning budget and a senior, no-nonsense team",
    ],
  },
  qa: {
    shortDescription:
      "Raise the quality bar across the product - designing test strategy, building automated coverage and partnering with engineering to ship with confidence.",
    responsibilities: [
      "Design and maintain automated test suites (UI, API, E2E)",
      "Define test strategy and coverage for new features",
      "Run exploratory testing on critical user journeys",
      "Integrate tests into CI/CD and triage failures fast",
      "Champion quality practices across the squad",
    ],
    requirements: [
      "Solid experience with at least one automation framework",
      "Strong programming basics (TypeScript, Java or Python)",
      "Working knowledge of REST APIs and SQL",
      "Experience with CI/CD tools and version control",
      "English at B2+ for technical communication",
    ],
    niceToHave: [
      "Performance or security testing exposure",
      "Experience with contract testing or test data management",
      "Mobile testing background",
    ],
  },
  java: {
    shortDescription:
      "Build resilient backend services in modern Java - designing clean APIs, owning your services in production and contributing to the platform&apos;s long-term health.",
    responsibilities: [
      "Design, build and operate Java microservices",
      "Define clean APIs and integrate with internal and external systems",
      "Write tests at unit, integration and contract level",
      "Profile, tune and harden services for production load",
      "Participate in code review and technical decisions",
    ],
    requirements: [
      "4+ years with modern Java (17+) and Spring Boot",
      "Solid relational database experience (PostgreSQL, MySQL)",
      "Hands-on with cloud (AWS / Azure / GCP) and CI/CD",
      "Comfort with testing pyramids and clean code practices",
      "English at B2+ for daily collaboration",
    ],
    niceToHave: [
      "Event-driven architecture experience (Kafka, RabbitMQ)",
      "Containers, Kubernetes and infrastructure as code",
      "Background in retail, fintech or high-throughput systems",
    ],
  },
  fallback: {
    shortDescription:
      "Join a senior, remote-first squad delivering modern digital products - high autonomy, strong engineering culture and direct impact on what gets built.",
    responsibilities: [
      "Own meaningful slices of the product end-to-end",
      "Collaborate closely with product, design and engineering peers",
      "Contribute to technical and process decisions",
      "Help raise the quality bar across the team",
      "Share knowledge through reviews, docs and pairing",
    ],
    requirements: [
      "Proven experience in a similar role on production-grade systems",
      "Strong fundamentals in your craft and best practices",
      "Self-directed working style suited to a remote team",
      "Clear written and verbal communication",
      "English at B2+ for everyday collaboration",
    ],
    niceToHave: [
      "Experience working in distributed, cross-cultural teams",
      "Exposure to modern agile practices and continuous delivery",
      "Mentorship or technical leadership background",
    ],
  },
};

const BENEFITS = [
  "100% remote work, anywhere in Poland or the CE region",
  "B2B or contract of employment - your choice",
  "Direct line to the recruitment team, not a faceless agency",
  "Modern tooling, paid learning budget and conference days",
  "Senior, no-nonsense team and short feedback loops",
];

const stripParens = (s: string) => s.replace(/\s*\([^)]*\)/g, "").trim();

const enrich = (j: RawJob): EnrichedJob => {
  const cleanTitle = stripParens(j.title);
  const role = detectRole(cleanTitle);
  const profile = ROLE_PROFILES[role];
  return {
    ...j,
    title: cleanTitle,
    role,
    location: j.workmode === "On-site" ? j.location : "REMOTE",
    slug: slugify(cleanTitle),
    shortDescription: profile.shortDescription,
    responsibilities: profile.responsibilities,
    requirements: profile.requirements,
    niceToHave: profile.niceToHave,
    benefits: profile.benefits ?? BENEFITS,
  };
};

/**
 * Presentation transform: dedupe by title, hide upstream company name,
 * force location to REMOTE, attach a stable internal slug, and enrich
 * with role-aware copy. The source URL is kept on the record only as a
 * technical identifier and is never surfaced as a CTA.
 */
const toPresentation = (jobs: RawJob[]): EnrichedJob[] => {
  const seen = new Set<string>();
  const out: EnrichedJob[] = [];
  for (const j of jobs) {
    const enriched = enrich(j);
    const key = enriched.slug || enriched.title.toLowerCase().trim();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(enriched);
  }
  const order = new Map(out.map((j, i) => [j, i]));
  return out.sort((a, b) => {
    const aHot = a.hot ? -1 : 0;
    const bHot = b.hot ? -1 : 0;
    if (aHot !== bHot) return aHot - bHot;
    const aInternal = a.url.startsWith("internal://") ? -1 : 0;
    const bInternal = b.url.startsWith("internal://") ? -1 : 0;
    if (aInternal !== bInternal) return aInternal - bInternal;
    // hot roles keep the curated source order (newest promoted role first)
    if (a.hot && b.hot) return (order.get(a) ?? 0) - (order.get(b) ?? 0);
    return a.title.localeCompare(b.title);
  });
};

export async function fetchJobs(): Promise<EnrichedJob[]> {
  const internal = JJIT_SNAPSHOT.filter((j) => j.url.startsWith("internal://"));
  const proxy = (import.meta.env.VITE_JJIT_PROXY_URL as string | undefined)?.trim();
  if (proxy) {
    try {
      const res = await fetch(proxy, { headers: { accept: "application/json" } });
      if (res.ok) {
        const data = (await res.json()) as RawJob[];
        if (Array.isArray(data) && data.length) return toPresentation([...internal, ...data]);
      }
    } catch {
      /* fall through to snapshot */
    }
  }
  const rest = JJIT_SNAPSHOT.filter((j) => !j.url.startsWith("internal://"));
  return toPresentation([...internal, ...rest]);
}

export { INTERNAL_SOURCE_URL };
